//  example:
// top menu is i priority 0, everything else is priority 1 or higher
// <div v-sticky="{
//     priority: 1,
//     shrinkClass: 'filters-shrunk',
//     verticalShrink: true,
//     shrinkAmount: 0.85
// }" style="background-color: var(--beige-100)">
// don't forget to add background color to avoid content showing through
    

class StickyManager {
    constructor() {
        this.elements = new Map();
    }

    register(el, options) {
        this.elements.set(el, {
            options,
            isStuck: false,
            currentHeight: el.offsetHeight,
            originalHeight: el.offsetHeight
        });
    }

    unregister(el) {
        this.elements.delete(el);
    }

    getSortedElements() {
        return Array.from(this.elements.entries())
        .sort(([,a], [,b]) => a.options.priority - b.options.priority);
    }

    calculateStickyTop(targetEl) {
        let top = 0;
        const targetPriority = this.elements.get(targetEl)?.options.priority || 0;

        for (const [el, data] of this.elements) {
            if (data.options.priority < targetPriority && data.isStuck) {
                let visualHeight;
                
                if (data.options.priority === 0) {
                    visualHeight = 60;
                } else if (data.options.verticalShrink) {
                    visualHeight = data.originalHeight * data.options.shrinkAmount;
                } else {
                    visualHeight = data.originalHeight;
                }
                top += visualHeight;
            }
        }

        return top;
    }

    updateElement(el, isStuck) {
        if (this.elements.has(el)) {
            const data = this.elements.get(el);
            data.isStuck = isStuck;
            
            if (data.options.priority === 0 && isStuck) {
                data.currentHeight = 60;
            } else {
                data.currentHeight = el.offsetHeight;
            }
            
            if (!isStuck) {
                data.originalHeight = el.offsetHeight;
            }
        }
    }

    updateAllElements() {
        const sortedElements = this.getSortedElements();
        
        for (const [el] of sortedElements) {
            if (el._updateStuckState) {
                el._updateStuckState();
            }
        }

        for (const [el] of sortedElements) {
            if (el._updatePosition) {
                el._updatePosition();
            }
        }
    }
}

const stickyManager = new StickyManager();

export const vSticky = {
    mounted(el, binding) {
        const defaultOptions = {
            topSpacing: 0,
            shrinkClass: 'sticky-shrink',
            shrinkThreshold: 50,
            zIndex: binding.value?.priority === 0 ? 99 : 10,
            priority: 0,
            verticalShrink: true,
            shrinkAmount: 0.85
        };

        const options = binding.value ? { ...defaultOptions, ...binding.value } : defaultOptions;

        const rect = el.getBoundingClientRect();
        const initialTop = rect.top + window.scrollY;
        
        stickyManager.register(el, options);

        const computedStyle = window.getComputedStyle(el);
        const originalTransform = computedStyle.transform !== 'none' ? computedStyle.transform : '';
        const originalHeight = el.offsetHeight;
        const originalComputedHeight = computedStyle.height;
        const originalPaddingTop = parseInt(computedStyle.paddingTop) || 0;

        el._originalTransform = originalTransform;
        el._originalHeight = originalHeight;
        el._originalComputedHeight = originalComputedHeight;
        el._originalPaddingTop = originalPaddingTop;
        el._stickyOptions = options;
        el._initialTop = initialTop;

        el.style.position = 'sticky';
        el.style.zIndex = options.zIndex;
        
        if (options.priority === 0) {
            el.style.transition = options.verticalShrink ? 'height 0.4s cubic-bezier(0.4, 0, 0.2, 1)' : 'none';
            el.style.boxSizing = 'border-box';
        } else {
            el.style.transition = options.verticalShrink ? 'transform 0.3s ease, padding-top 0.3s ease' : 'none';
            el.style.transformOrigin = 'top center';
        }

        let isCurrentlyStuck = false;
        let spacerEl = null;

        const getMainSection = () => el.closest('#main') || document.getElementById('main');

        const updateFixedPosition = () => {
            if (options.priority !== 0 || !isCurrentlyStuck) return;
            const main = getMainSection();
            if (!main) return;
            const rect = main.getBoundingClientRect();
            el.style.left = `${rect.left}px`;
            el.style.width = `${rect.width}px`;
        };

        const onResize = () => {
            updateFixedPosition();
        };

        const updateStuckState = () => {
            const scrollTop = window.scrollY;
    
            const targetStickyTop = stickyManager.calculateStickyTop(el);
            const effectiveThreshold = options.priority === 0 ? initialTop : initialTop - targetStickyTop;
    
            const shouldBecomeSticky = options.priority === 0 
                ? scrollTop > effectiveThreshold 
                : scrollTop >= effectiveThreshold;
            
            if (shouldBecomeSticky !== isCurrentlyStuck) {
                isCurrentlyStuck = shouldBecomeSticky;
                
                if (shouldBecomeSticky) {
                    el.classList.add(options.shrinkClass);
                    
                    if (options.priority === 0) {
                        el.style.position = 'fixed';
                        el.style.top = `${targetStickyTop + options.topSpacing}px`;
                        updateFixedPosition();
                        if (!spacerEl && el.parentNode) {
                            spacerEl = document.createElement('div');
                            spacerEl.setAttribute('data-sticky-spacer', '');
                            spacerEl.style.cssText = 'height:60px;flex-shrink:0;pointer-events:none;';
                            el.parentNode.insertBefore(spacerEl, el);
                            el._spacerEl = spacerEl;
                        }
                    }
                    
                    if (options.verticalShrink) {
                        if (options.priority === 0) {
                            el.style.height = '60px';
                            el.style.overflow = 'visible';
                        } else {
                            const scaleTransform = `scaleY(${options.shrinkAmount})`;
                            const newTransform = originalTransform ? `${originalTransform} ${scaleTransform}` : scaleTransform;
                            el.style.transform = newTransform;
                            el.style.paddingTop = `${originalPaddingTop + 8}px`;
                        }
                    }
                } else {
                    el.classList.remove(options.shrinkClass);
                    
                    if (options.priority === 0) {
                        el.style.position = 'sticky';
                        el.style.top = `${targetStickyTop + options.topSpacing}px`;
                        el.style.left = '';
                        el.style.width = '';
                        if (spacerEl && spacerEl.parentNode) {
                            spacerEl.parentNode.removeChild(spacerEl);
                            spacerEl = null;
                        }
                    }
                    
                    if (options.verticalShrink) {
                        if (options.priority === 0) {
                            el.style.height = '';
                            setTimeout(() => {
                                const currentHeight = el.offsetHeight;
                                if (currentHeight !== originalHeight) {
                                    el.style.height = originalHeight + 'px';
                                }
                            }, 50);
                            el.style.overflow = '';
                        } else {
                            el.style.transform = originalTransform || '';
                            el.style.paddingTop = `${originalPaddingTop}px`;
                        }
                    }
                }
                
                stickyManager.updateElement(el, shouldBecomeSticky);
            }
        };

        const updatePosition = () => {
            const stickyTop = stickyManager.calculateStickyTop(el) + options.topSpacing;
            el.style.top = `${stickyTop}px`;
            if (options.priority === 0 && isCurrentlyStuck) {
                updateFixedPosition();
            }
        };

        el._updateStuckState = updateStuckState;
        el._updatePosition = updatePosition;

        updatePosition();
        updateStuckState();
        window.addEventListener('resize', onResize);
        el._onResize = onResize;
    },

    unmounted(el) {
        stickyManager.unregister(el);
        window.removeEventListener('resize', el._onResize);
        if (el._spacerEl && el._spacerEl.parentNode) {
            el._spacerEl.parentNode.removeChild(el._spacerEl);
        }

        if (el._stickyOptions?.shrinkClass) {
            el.classList.remove(el._stickyOptions.shrinkClass);
        }

        if (el._stickyOptions?.priority === 0) {
            el.style.position = '';
            el.style.top = '';
            el.style.left = '';
            el.style.width = '';
            el.style.height = el._originalHeight + 'px';
            el.style.overflow = '';
        } else {
            if (el._originalTransform !== undefined) {
                el.style.transform = el._originalTransform;
            }
            if (el._originalPaddingTop !== undefined) {
                el.style.paddingTop = `${el._originalPaddingTop}px`;
            }
        }

        delete el._originalTransform;
        delete el._originalHeight;
        delete el._originalComputedHeight;
        delete el._originalPaddingTop;
        delete el._stickyOptions;
        delete el._initialTop;
        delete el._updateStuckState;
        delete el._updatePosition;
        delete el._onResize;
        delete el._spacerEl;
    },

    updated(el, binding) {
        if (JSON.stringify(binding.value) !== JSON.stringify(binding.oldValue)) {
            vSticky.unmounted(el);
            vSticky.mounted(el, binding);
        }
    }
};

let ticking = false;
const handleScroll = () => {
    if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
            stickyManager.updateAllElements();
            ticking = false;
        });
    }
};

window.addEventListener('scroll', handleScroll, { passive: true });

export default {
    install(app) {
        app.directive('sticky', vSticky);
    }
};